import { callApi } from 'services/ApiCommon';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMdlrmdlUserInfo } from 'services/UserApiService';
import { setUserConfigCn, setFavoriteList, setFavMdlrmdlCn } from 'store/auth/userSlice';

// App.js에서 User 컴포넌트를 import하여 사용합니다.
const User = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const favoriteList = useSelector((state) => state.auth.user.favoriteList || []);
  const favMdlrmdlCn = useSelector((state) => state.auth.user.favMdlrmdlCn || []);
  const userConfigCn = useSelector((state) => state.auth.user.userConfigCn || {});
  const { apiListLoaded } = useSelector((state) => state.base.common);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // 이전 userConfigCn 값을 저장하여 변경 감지
  const prevUserConfigCnRef = useRef(null);


  // ✅ 초기 사용자 데이터 로드 - API 로드 의존성 추가
  useEffect(() => {
    const loadUserData = async () => {
      // API가 로드되지 않았으면 데이터 로드를 시도하지 않음
      if (!apiListLoaded) {
        return;
      }

      if (!user || !user.userId) return;

      try {
        const userInfo = await fetchMdlrmdlUserInfo(user.userId);

        if (userInfo && userInfo.length > 0) {
          const userData = userInfo[0];

          // 즐겨찾기 메뉴 처리
          if (userData.favMnuCn) {
            try {
              const parsedFavMenu = typeof userData.favMnuCn === 'string' 
                ? JSON.parse(userData.favMnuCn) 
                : userData.favMnuCn;
              
              if (Array.isArray(parsedFavMenu)) {
                dispatch(setFavoriteList(parsedFavMenu));
              }
            } catch (error) {
              console.error("즐겨찾기 메뉴 파싱 실패:", error);
              dispatch(setFavoriteList([]));
            }
          }

          // 즐겨찾기 모델러모델 처리
          if (userData.favMdlrmdlCn) {
            try {
              const parsedFavModels = typeof userData.favMdlrmdlCn === 'string' 
                ? JSON.parse(userData.favMdlrmdlCn) 
                : userData.favMdlrmdlCn;
              
              if (Array.isArray(parsedFavModels)) {
                dispatch(setFavMdlrmdlCn(parsedFavModels));
              }
            } catch (error) {
              console.error("즐겨찾기 모델러모델 파싱 실패:", error);
              dispatch(setFavMdlrmdlCn([]));
            }
          }

          // 사용자 설정 처리
          if (userData.userCfgCn) {
            try {
              const parsedConfig = typeof userData.userCfgCn === 'string'
                ? JSON.parse(userData.userCfgCn)
                : userData.userCfgCn;
              
              dispatch(setUserConfigCn(parsedConfig));
            } catch (error) {
              console.error("사용자 설정 파싱 실패:", error);
            }
          }
        }
      } catch (error) {
        console.error("사용자 정보 로드 실패:", error);
      } finally {
        // 초기 로딩 완료
        setIsInitialLoading(false);
      }
    };

    loadUserData();
  }, [user?.userId, dispatch, apiListLoaded]);

  // API 로드 상태 변경 감지

  // ✅ 공통 저장 함수 - useCallback으로 메모이제이션
  const saveUserData = useCallback(async (rawData, label) => {
    // API가 로드되지 않았거나 초기 로딩 중일 때는 저장하지 않음
    if (!apiListLoaded || !user || !user.userId || !rawData || isInitialLoading) {
      return;
    }

    const transformedData = {
      usrid: user.userId,
    };

    Object.entries(rawData).forEach(([key, value]) => {
      if (Array.isArray(value) || typeof value === 'object') {
        // ✅ JSON 변환 대상
        transformedData[key] = JSON.stringify(value);
      } else {
        transformedData[key] = value;
      }
    });

    const payload = {
      createdRows: [],
      updatedRows: [transformedData],
      deletedRows: [],
    };

    try {
      await callApi('saveMdlrmdlUserInfo', payload);
    } catch (error) {
      console.error(`${label} 저장 실패:`, error);
    }
  }, [apiListLoaded, user, isInitialLoading]);
  
  // 즐겨찾기 메뉴 저장
  useEffect(() => {
    if (user?.userId && Array.isArray(favoriteList) && favoriteList.length > 0 && !isInitialLoading && apiListLoaded) {
      saveUserData({ FAV_MNU_CN: favoriteList }, '즐겨찾기 메뉴');
    }
  }, [favoriteList, user?.userId, isInitialLoading, apiListLoaded, saveUserData]);

  // 즐겨찾기 모델러모델 저장
  useEffect(() => {
    if (user?.userId && Array.isArray(favMdlrmdlCn) && favMdlrmdlCn.length > 0 && !isInitialLoading && apiListLoaded) {
      saveUserData({ FAV_MDLRMDL_CN: favMdlrmdlCn }, '즐겨찾기 모델러모델');
    }
  }, [favMdlrmdlCn, user?.userId, isInitialLoading, apiListLoaded, saveUserData]);

  // 사용자 설정 저장 (userConfigCn 변경 감지)
  useEffect(() => {
    const currentConfigJson = JSON.stringify(userConfigCn);
    const prevConfigJson = prevUserConfigCnRef.current;


    // 초기 로딩이 완료되고, 이전 값과 현재 값이 다를 때만 저장
    if (!isInitialLoading && apiListLoaded && user?.userId) {
      // 이전 값이 있고 현재 값과 다를 때만 저장 (초기 로드 제외)
      if (prevConfigJson !== null && currentConfigJson !== prevConfigJson) {
        saveUserData({ USER_CFG_CN: userConfigCn }, '사용자 설정');
      } else if (prevConfigJson === null) {
      } else {
      }
    }

    // 현재 값을 이전 값으로 저장
    prevUserConfigCnRef.current = currentConfigJson;
  }, [userConfigCn, isInitialLoading, apiListLoaded, user?.userId, saveUserData]);
 
  return null; // 렌더링 없이 훅만 작동
};

export default User;